<template>
  <div
    class="overlay"
    @click.self="$emit('hide')"
  >
    <div class="modal">
      <template v-if="passAccepted">
        <img
          src="@/assets/close.svg"
          class="close"
          @click="$emit('hide')"
        >
        <div class="items">
          <a
            class="item"
            href="https://justadudewhohacks.github.io/face-api.js/webcam_face_tracking/"
            target="_blank"
          >
            <img
              src="@/assets/bug_report_black.svg"
              class="icon"
            >
            <span>Face detection debugger</span>
          </a>
          <div
            v-if="fullscreenEnabled"
            class="item"
            @click="toggleFullscreen"
          >
            <template v-if="isFullScreenOn">
              <img
                src="@/assets/fullscreen_exit_black.svg"
                class="icon"
              >
              <span>Exit Fullscreen</span>
            </template>
            <template v-else>
              <img
                src="@/assets/fullscreen_black.svg"
                class="icon"
              >
              <span>Enter Fullscreen</span>
            </template>
          </div>
        </div>
      </template>
      <div
        v-else
        class="pass-form"
      >
        <div class="title">
          Password
        </div>
        <div>
          <input
            ref="passRef"
            v-model="password"
            :class="{ error: error.length }"
            type="password"
            class="input"
            @keyup.enter="checkPass"
          >
          <div
            v-if="error"
            class="error"
          >
            {{ error }}
          </div>
        </div>
        <div class="buttons">
          <div
            class="button"
            @click="$emit('hide')"
          >
            Cancel
          </div>
          <div
            class="button confirm"
            @click="checkPass"
          >
            Confirm
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import sha512 from 'sha512';
import useConfig from '@/composables/useConfig';

export default defineComponent({
  name: 'DebugModalComponent',

  emits: ['hide'],

  setup(props, { emit }) {
    const { initialConfig } = useConfig();

    const passAccepted = ref(false);
    const error = ref('');
    const password = ref('');
    const passRef = ref(null);
    const fullscreenEnabled = document.fullscreenEnabled
      || document.webkitFullscreenEnabled
      || document.mozFullScreenEnabled;
    const isFullScreenOn = ref(document.fullscreenElement
      || document.webkitFullscreenElement
      || document.mozFullScreenElement);

    function checkPass() {
      if (!password.value.trim()) {
        error.value = 'Field is required!';
        return;
      }

      const hash = sha512(`${password.value}${initialConfig.value.credentials.salt}`);
      if (hash.toString('hex') !== initialConfig.value.credentials.hash) {
        error.value = 'Invalid password!';
        return;
      }

      error.value = '';
      passAccepted.value = true;
    }

    function toggleFullscreen() {
      if (document.documentElement.requestFullscreen) {
        if (isFullScreenOn.value) {
          document.exitFullscreen();
        } else {
          document.documentElement.requestFullscreen();
        }
      } else if (document.documentElement.webkitRequestFullscreen) {
        if (isFullScreenOn.value) {
          document.webkitExitFullscreen();
        } else {
          document.documentElement.webkitRequestFullscreen();
        }
      } else if (document.documentElement.mozRequestFullScreen) {
        if (isFullScreenOn.value) {
          document.mozCancelFullScreen();
        } else {
          document.documentElement.mozRequestFullScreen();
        }
      }
    }

    function handleGlobalKeyPresses(evt) {
      const key = evt.key.toLowerCase();
      if (key === 'escape') {
        emit('hide');
      }
    }

    function handleFullScreenChange() {
      isFullScreenOn.value = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement;
    }

    onMounted(() => {
      passRef.value.focus();
      document.body.addEventListener('keyup', handleGlobalKeyPresses);
      document.addEventListener('fullscreenchange', handleFullScreenChange);
      document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    });

    onBeforeUnmount(() => {
      document.body.removeEventListener('keyup', handleGlobalKeyPresses);
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
    });

    return {
      passAccepted,
      password,
      error,
      passRef,
      isFullScreenOn,
      fullscreenEnabled,
      checkPass,
      toggleFullscreen,
    };
  },
});
</script>

<style lang="scss" scoped>
  .overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999999;
    padding: 20px;
  }

  .modal {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    display: flex;
    justify-items: center;
    align-items: center;
    width: 620px;
    height: 300px;
    max-width: 100%;
    max-height: 100%;
    background-color: #fff;
    border: 1px solid #E1E6EC;
    box-shadow: 0 8px 24px -6px rgba(0, 0, 0, 0.16), 0 0 1px rgba(0, 0, 0, 0.4);
    border-radius: 12px;
    padding: 60px 78px;
    text-align: center;

    @media screen and (orientation: portrait) and (max-width: $tablet_breakpoint - 1px) {
      padding: 32px 16px;
    }
  }

  .pass-form {
    width: 100%;
  }

  .title {
    margin-bottom: 12px;
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 36px;
    color: #2A3746;
  }

  .input {
    width: 100%;
    height: 48px;
    line-height: 48px;
    background: #FFFFFF;
    border: 1px solid #E1E6EC;
    outline: 1px solid transparent;
    border-radius: 4px;
    color: #2A3746;
    font-weight: 400;
    font-size: 16px;
    padding: 0 16px;

    &:focus {
      border: 1px solid #1E58F2;
      outline: 1px solid #1E58F2;
    }

    &.error {
      border: 1px solid #FF5340;
      outline: 1px solid #FF5340;
    }
  }

  .error {
    margin-top: 8px;
    color: #FF1900;
  }

  .buttons {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    margin-top: 32px;
  }

  .button {
    width: 200px;
    height: 48px;
    background: #FFFFFF;
    border: 2px solid #1E58F2;
    border-radius: 8px;
    font-weight: 500;
    font-size: 16px;
    line-height: 48px;
    letter-spacing: 0.15px;
    color: #1E58F2;
    text-align: center;
    cursor: pointer;

    &.confirm {
      color: #fff;
      background-color: #1E58F2;
    }

    @media screen and (orientation: portrait) and (max-width: $tablet_breakpoint - 1px) {
      width: 100%;
      margin-bottom: 8px;
    }
  }

  .close {
    position: absolute;
    right: 20px;
    top: 20px;
    cursor: pointer;
  }

  .items {
    width: 100%;
  }

  .item {
    cursor: pointer;
    height: 44px;
    line-height: 44px;
    padding: 0 16px;
    text-align: left;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    color: #2A3746;
    text-decoration: none;

    &:hover {
      background-color: #EDF2FE;
    }
  }

  .icon {
    margin-right: 16px;
    width: 32px;
    height: 32px;
  }

  .item-with-message {
    position: relative;
    width: 100%;

    .text {
      position: absolute;
      font-size: 12px;
      line-height: 12px;
      color: #26A69A;
    }
  }
</style>
